import type { ParallaxConfig } from '@/systems/ParallaxBackground';
import type { StallPlacement } from '@/data/stalls';
import type { PropPlacement, HealthPickupPlacement, HazardZonePlacement } from '@/data/props';
import { STAGE_9_GAUNTLET, type FinaleGauntletConfig } from '@/systems/FinaleGauntlet';

/**
 * Per-stage configuration. Typed data (not raw JSON) so the parallax + level
 * layout is checked against the systems that consume it. Stages 2–9 land via
 * their own tickets (MY-1411+); only Stage 1 is fully specced here.
 */

/** One enemy group inside a wave: how many of a given archetype to spawn. */
export interface EnemyGroup {
  id: string;
  count: number;
}

/** A single wave definition: the enemy composition to spawn together. */
export interface WaveDef {
  enemies: EnemyGroup[];
}

/**
 * A stage section groups waves into a checkpoint unit.
 * Clearing all waves in a section triggers a checkpoint save.
 * On death/continue the player restarts the current section, not the whole stage.
 */
export interface SectionDef {
  waves: WaveDef[];
  /** Optional elite enemy spawned after all section waves clear (before the next section). */
  eliteId?: string;
}

/**
 * Stage-specific gameplay hazard applied on top of normal wave logic.
 * TODO(MY-1668): extend with 'rising-water' | 'wind-gust' etc. as new stages land.
 */
export type StageHazard = 'moving-carriage' | 'lift-climb';

/**
 * Stage-specific scoring objective. Data-flagged; full engine integration per
 * follow-up ticket. 'intercept-ribs': periodic RIB markers approach the
 * shoreline — clearing them before they land awards bonus score.
 * TODO(MY-1671): wire RIB marker spawner + intercept scoring in StageScene.
 */
export type StageObjective = 'intercept-ribs';

export interface StageConfig {
  id: number;
  name: string;
  parallax: ParallaxConfig;
  /** Ordered list of waves to play through before the stage is cleared. */
  waves: WaveDef[];
  /**
   * Optional checkpoint sections. When present, overrides flat `waves`:
   * each section is a discrete checkpoint unit — clearing all of a section's
   * waves sets the respawn point at the start of the next section.
   * Back-compat: if absent, the flat `waves` array is treated as one section.
   */
  sections?: SectionDef[];
  /** Enemy archetype id for the stage boss; spawned after all waves clear. */
  bossId?: string;
  /** Optional stage hazard; when set StageScene applies the matching effect. */
  hazard?: StageHazard;
  /** Optional scoring objective layered on top of wave combat. */
  objective?: StageObjective;
  /**
   * Optional market stall placements for the level arena.
   * Only populated for Camden (Stage 1); other stages leave this undefined.
   */
  marketStalls?: readonly StallPlacement[];
  /**
   * Optional generic destructible prop placements (bins, crates, phone boxes, etc.).
   * StageScene renders and hit-detects these on every stage from this config.
   */
  props?: readonly PropPlacement[];
  /**
   * Optional standalone health pickups placed in the level arena.
   * Heals the nearest player on contact (no attack required).
   */
  healthPickups?: readonly HealthPickupPlacement[];
  /**
   * Optional permanent hazard zones in the stage (third rails, acid vats, etc.).
   * Thrown enemies that land inside one of these zones take bonus damage.
   */
  hazardZones?: readonly HazardZonePlacement[];
  /**
   * Id of the named elite mini-boss lieutenant for this stage (MY-1782).
   * Spawned by the spawn system at eliteWaveIndex; drops weapon + health on KO.
   */
  eliteId?: string;
  /**
   * Id of the themed rideable mount for this stage (MY-1890).
   * Resolved against the VEHICLES registry; spawned by StageScene at level mid-point.
   * Absent on stages with no assigned mount.
   */
  rideableId?: string;
  /**
   * 0-based wave index at which the elite is introduced mid-stage (MY-1782).
   * Defaults to mid-point if absent; must be within the waves array bounds.
   */
  eliteWaveIndex?: number;
  /**
   * Optional multi-phase finale gauntlet (MY-1976).
   * When present, replaces the direct `bossId` final encounter with a
   * structured gauntlet: Cordon → Old Faces boss rush → Praetor handoff.
   * Only Stage 9 uses this; all other stages use plain `bossId`.
   */
  finaleGauntlet?: FinaleGauntletConfig;
}

const STAGE_1: StageConfig = {
  id: 1,
  name: 'CAMDEN TOWN',
  parallax: {
    levelWidth: 2560,
    sky: 0x14181f,
    layers: [{ factor: 0.4, image: 'bg_s1_scene', color: 0x222838, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'camden-trader', count: 3 }] },
    {
      enemies: [
        { id: 'camden-trader', count: 2 },
        { id: 'albanian-smuggler', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'camden-trader', count: 1 },
        { id: 'albanian-smuggler', count: 2 },
      ],
    },
    { enemies: [{ id: 'albanian-smuggler', count: 3 }] },
  ],
  // MY-1791: 3-area sections for ~20-min playthrough (20 waves total).
  // No elite assigned to Stage 1 (MY-1782 covers stages 2–9 only).
  sections: [
    // Area 1 — Camden Market Approach (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'camden-trader', count: 2 }] },
        { enemies: [{ id: 'camden-trader', count: 3 }] },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 4 },
            { id: 'albanian-smuggler', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Stalls (7 waves, checkpoint after; no elite for Stage 1)
    {
      waves: [
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
        { enemies: [{ id: 'albanian-smuggler', count: 3 }] },
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 3 },
          ],
        },
        { enemies: [{ id: 'albanian-smuggler', count: 4 }] },
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — Canal Side (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 3 },
          ],
        },
        { enemies: [{ id: 'albanian-smuggler', count: 4 }] },
        {
          enemies: [
            { id: 'camden-trader', count: 4 },
            { id: 'albanian-smuggler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'camden-trader', count: 2 },
            { id: 'albanian-smuggler', count: 4 },
          ],
        },
        { enemies: [{ id: 'albanian-smuggler', count: 5 }] },
        {
          enemies: [
            { id: 'camden-trader', count: 3 },
            { id: 'albanian-smuggler', count: 3 },
          ],
        },
      ],
    },
  ],
  bossId: 'the-bargee',
  rideableId: 'market-scooter',
  // Canal runs along the right edge of the Camden level (Area 3 — Canal Side).
  hazardZones: [{ hazardId: 's1-canal', xFrac: 0.88, wFrac: 0.08 }],
  marketStalls: [
    { stallId: 'camden-fish-stall', xFrac: 0.25 },
    { stallId: 'camden-kebab-van', xFrac: 0.4 },
    { stallId: 'camden-vinyl-stall', xFrac: 0.6 },
    { stallId: 'camden-chips-stall', xFrac: 0.75 },
  ],
};

const STAGE_2: StageConfig = {
  id: 2,
  name: 'SOHO AFTER DARK',
  parallax: {
    levelWidth: 2880,
    sky: 0x0a0612,
    layers: [{ factor: 0.4, image: 'bg_s2_scene', color: 0x1b0f2e, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'soho-tout', count: 3 }] },
    {
      enemies: [
        { id: 'soho-tout', count: 2 },
        { id: 'club-bouncer', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'soho-tout', count: 1 },
        { id: 'club-bouncer', count: 2 },
      ],
    },
    {
      enemies: [
        { id: 'club-bouncer', count: 2 },
        { id: 'bailiff-heavy', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'soho-tout', count: 2 },
        { id: 'club-bouncer', count: 2 },
      ],
    },
  ],
  // MY-1791: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — Soho Streets (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'soho-tout', count: 3 }] },
        { enemies: [{ id: 'soho-tout', count: 4 }] },
        {
          enemies: [
            { id: 'soho-tout', count: 2 },
            { id: 'club-bouncer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 3 },
            { id: 'club-bouncer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 2 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 4 },
            { id: 'club-bouncer', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — Nightclub Row (7 waves, soho-headhunter elite at end, checkpoint after)
    {
      eliteId: 'soho-headhunter',
      waves: [
        {
          enemies: [
            { id: 'soho-tout', count: 2 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 3 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        { enemies: [{ id: 'club-bouncer', count: 3 }] },
        {
          enemies: [
            { id: 'soho-tout', count: 2 },
            { id: 'club-bouncer', count: 2 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 3 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'club-bouncer', count: 3 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 4 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — The Door (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'soho-tout', count: 3 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'club-bouncer', count: 3 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 4 },
            { id: 'club-bouncer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 2 },
            { id: 'club-bouncer', count: 3 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 4 },
            { id: 'club-bouncer', count: 3 },
          ],
        },
        {
          enemies: [
            { id: 'club-bouncer', count: 4 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'soho-tout', count: 3 },
            { id: 'club-bouncer', count: 3 },
            { id: 'bailiff-heavy', count: 1 },
          ],
        },
      ],
    },
  ],
  bossId: 'doorman-rex',
  eliteId: 'soho-headhunter',
  eliteWaveIndex: 2,
  props: [
    { propId: 'soho-bin', xFrac: 0.28 },
    { propId: 'soho-phone-box', xFrac: 0.52 },
    { propId: 'soho-bin', xFrac: 0.72 },
  ],
  healthPickups: [{ xFrac: 0.42, heal: 15 }],
};

const STAGE_3: StageConfig = {
  id: 3,
  name: 'BERMONDSEY DOCKS',
  parallax: {
    levelWidth: 3000,
    sky: 0x0c1014,
    layers: [{ factor: 0.4, image: 'bg_s3_scene', color: 0x172029, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'crate-runner', count: 3 }] },
    {
      enemies: [
        { id: 'crate-runner', count: 2 },
        { id: 'dockyard-heavy', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'crate-runner', count: 1 },
        { id: 'dockyard-heavy', count: 2 },
      ],
    },
    { enemies: [{ id: 'dockyard-heavy', count: 3 }] },
    {
      enemies: [
        { id: 'crate-runner', count: 2 },
        { id: 'dockyard-heavy', count: 2 },
      ],
    },
  ],
  // MY-1791: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — Bermondsey Wharf (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'crate-runner', count: 3 }] },
        { enemies: [{ id: 'crate-runner', count: 4 }] },
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 3 },
            { id: 'dockyard-heavy', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 4 },
            { id: 'dockyard-heavy', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Container Yard (7 waves, dock-foreman elite at end, checkpoint after)
    {
      eliteId: 'dock-foreman',
      waves: [
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 3 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        { enemies: [{ id: 'dockyard-heavy', count: 3 }] },
        {
          enemies: [
            { id: 'crate-runner', count: 3 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 3 },
          ],
        },
        { enemies: [{ id: 'dockyard-heavy', count: 4 }] },
        {
          enemies: [
            { id: 'crate-runner', count: 4 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — Deep Dock (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'crate-runner', count: 3 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 3 },
          ],
        },
        { enemies: [{ id: 'dockyard-heavy', count: 4 }] },
        {
          enemies: [
            { id: 'crate-runner', count: 4 },
            { id: 'dockyard-heavy', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'crate-runner', count: 2 },
            { id: 'dockyard-heavy', count: 4 },
          ],
        },
        { enemies: [{ id: 'dockyard-heavy', count: 5 }] },
        {
          enemies: [
            { id: 'crate-runner', count: 3 },
            { id: 'dockyard-heavy', count: 3 },
          ],
        },
      ],
    },
  ],
  bossId: 'the-ferryman',
  eliteId: 'dock-foreman',
  eliteWaveIndex: 2,
  props: [
    { propId: 'docks-crate', xFrac: 0.25 },
    { propId: 'docks-barrel', xFrac: 0.48 },
    { propId: 'docks-crate', xFrac: 0.68 },
  ],
  healthPickups: [{ xFrac: 0.35, heal: 10 }],
};

const STAGE_4: StageConfig = {
  id: 4,
  name: 'NORTHERN LINE',
  parallax: {
    levelWidth: 2720,
    sky: 0x070707,
    layers: [{ factor: 0.4, image: 'bg_s4_scene', color: 0x1a1206, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'platform-tough', count: 3 }] },
    {
      enemies: [
        { id: 'platform-tough', count: 2 },
        { id: 'track-rat-handler', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'platform-tough', count: 1 },
        { id: 'track-rat-handler', count: 2 },
      ],
    },
    { enemies: [{ id: 'track-rat-handler', count: 3 }] },
  ],
  // MY-1791: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — Platform Level (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'platform-tough', count: 3 }] },
        { enemies: [{ id: 'platform-tough', count: 4 }] },
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 3 },
            { id: 'track-rat-handler', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 4 },
            { id: 'track-rat-handler', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Tunnel (7 waves, rail-warden elite at end, checkpoint after)
    {
      eliteId: 'rail-warden',
      waves: [
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 3 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        { enemies: [{ id: 'track-rat-handler', count: 3 }] },
        {
          enemies: [
            { id: 'platform-tough', count: 3 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 3 },
          ],
        },
        { enemies: [{ id: 'track-rat-handler', count: 4 }] },
        {
          enemies: [
            { id: 'platform-tough', count: 4 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — Driver's Cab (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'platform-tough', count: 3 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 3 },
          ],
        },
        { enemies: [{ id: 'track-rat-handler', count: 4 }] },
        {
          enemies: [
            { id: 'platform-tough', count: 4 },
            { id: 'track-rat-handler', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'platform-tough', count: 2 },
            { id: 'track-rat-handler', count: 4 },
          ],
        },
        { enemies: [{ id: 'track-rat-handler', count: 5 }] },
        {
          enemies: [
            { id: 'platform-tough', count: 3 },
            { id: 'track-rat-handler', count: 3 },
          ],
        },
      ],
    },
  ],
  bossId: 'the-conductor',
  rideableId: 'tube-trolley',
  eliteId: 'rail-warden',
  eliteWaveIndex: 2,
  hazard: 'moving-carriage',
  props: [
    { propId: 'tube-locker', xFrac: 0.28 },
    { propId: 'tube-bin', xFrac: 0.5 },
    { propId: 'tube-locker', xFrac: 0.72 },
  ],
  healthPickups: [
    { xFrac: 0.38, heal: 10 },
    { xFrac: 0.62, heal: 10 },
  ],
  hazardZones: [
    { hazardId: 's4-third-rail', xFrac: 0.15, wFrac: 0.08 },
    { hazardId: 's4-third-rail', xFrac: 0.85, wFrac: 0.08 },
  ],
};

const STAGE_5: StageConfig = {
  id: 5,
  name: 'CROYDON SPICE LAB',
  parallax: {
    levelWidth: 2880,
    sky: 0x0a0f08,
    layers: [{ factor: 0.4, image: 'bg_s5_scene', color: 0x14210f, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'lab-muscle', count: 3 }] },
    {
      enemies: [
        { id: 'lab-muscle', count: 2 },
        { id: 'spiced-enforcer', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'lab-muscle', count: 1 },
        { id: 'spiced-enforcer', count: 2 },
      ],
    },
    { enemies: [{ id: 'spiced-enforcer', count: 3 }] },
    {
      enemies: [
        { id: 'lab-muscle', count: 2 },
        { id: 'spiced-enforcer', count: 2 },
        { id: 'county-runner', count: 1 },
        { id: 'county-enforcer', count: 1 },
      ],
    },
  ],
  // MY-1791: 3-area sections for ~20-min playthrough (20 waves total).
  // spiced-enforcer doubles as both regular enemy and section-2 elite (elite-tier archetype).
  sections: [
    // Area 1 — Lab Perimeter (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'lab-muscle', count: 3 }] },
        { enemies: [{ id: 'lab-muscle', count: 4 }] },
        {
          enemies: [
            { id: 'lab-muscle', count: 2 },
            { id: 'county-runner', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 3 },
            { id: 'county-runner', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 2 },
            { id: 'county-enforcer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 4 },
            { id: 'county-runner', count: 2 },
          ],
        },
      ],
    },
    // Area 2 — Processing Floor (7 waves, spiced-enforcer elite at end, checkpoint after)
    {
      eliteId: 'spiced-enforcer',
      waves: [
        {
          enemies: [
            { id: 'lab-muscle', count: 2 },
            { id: 'spiced-enforcer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 3 },
            { id: 'spiced-enforcer', count: 1 },
          ],
        },
        { enemies: [{ id: 'spiced-enforcer', count: 2 }] },
        {
          enemies: [
            { id: 'lab-muscle', count: 2 },
            { id: 'spiced-enforcer', count: 2 },
            { id: 'county-runner', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 3 },
            { id: 'spiced-enforcer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'spiced-enforcer', count: 3 },
            { id: 'county-enforcer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 4 },
            { id: 'spiced-enforcer', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — The Cook's Den (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'lab-muscle', count: 3 },
            { id: 'spiced-enforcer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'spiced-enforcer', count: 3 },
            { id: 'county-runner', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 2 },
            { id: 'spiced-enforcer', count: 3 },
            { id: 'county-enforcer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 4 },
            { id: 'spiced-enforcer', count: 2 },
            { id: 'county-runner', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'spiced-enforcer', count: 3 },
            { id: 'county-enforcer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'lab-muscle', count: 3 },
            { id: 'spiced-enforcer', count: 3 },
            { id: 'county-runner', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'spiced-enforcer', count: 4 },
            { id: 'county-enforcer', count: 2 },
          ],
        },
      ],
    },
  ],
  bossId: 'the-chemist',
  eliteId: 'spiced-enforcer',
  eliteWaveIndex: 2,
  props: [
    { propId: 'croydon-gas-canister', xFrac: 0.25 },
    { propId: 'croydon-chem-barrel', xFrac: 0.48 },
    { propId: 'croydon-gas-canister', xFrac: 0.7 },
  ],
  healthPickups: [{ xFrac: 0.38, heal: 12 }],
  hazardZones: [{ hazardId: 's5-acid-vat', xFrac: 0.5, wFrac: 0.12 }],
};

const STAGE_6: StageConfig = {
  id: 6,
  name: 'BETHNAL GREEN',
  parallax: {
    levelWidth: 2880,
    sky: 0x120a0c,
    layers: [{ factor: 0.4, image: 'bg_s6_scene', color: 0x241318, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'cadre-agitator', count: 3 }] },
    {
      enemies: [
        { id: 'cadre-agitator', count: 2 },
        { id: 'cadre-marshal', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'cadre-agitator', count: 1 },
        { id: 'cadre-marshal', count: 2 },
      ],
    },
    { enemies: [{ id: 'cadre-marshal', count: 3 }] },
    {
      // MY-1885: Riot Sergeant joins the final flat wave alongside provocateur
      enemies: [
        { id: 'cadre-agitator', count: 2 },
        { id: 'cadre-marshal', count: 2 },
        { id: 'provocateur', count: 1 },
        { id: 'riot-sergeant', count: 1 },
      ],
    },
  ],
  // MY-1792: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — The March (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'cadre-agitator', count: 3 }] },
        { enemies: [{ id: 'cadre-agitator', count: 4 }] },
        {
          enemies: [
            { id: 'cadre-agitator', count: 2 },
            { id: 'cadre-marshal', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 3 },
            { id: 'cadre-marshal', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 2 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 4 },
            { id: 'cadre-marshal', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — Town Hall Square (7 waves, cadre-captain elite at end, checkpoint after)
    {
      eliteId: 'cadre-captain',
      waves: [
        {
          enemies: [
            { id: 'cadre-agitator', count: 2 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 3 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
        { enemies: [{ id: 'cadre-marshal', count: 3 }] },
        {
          enemies: [
            { id: 'cadre-agitator', count: 2 },
            { id: 'cadre-marshal', count: 2 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 3 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-marshal', count: 3 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 4 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — The Commune (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'cadre-agitator', count: 3 },
            { id: 'cadre-marshal', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-marshal', count: 3 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 2 },
            { id: 'cadre-marshal', count: 3 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 4 },
            { id: 'cadre-marshal', count: 2 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-marshal', count: 3 },
            { id: 'provocateur', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-agitator', count: 3 },
            { id: 'cadre-marshal', count: 3 },
            { id: 'provocateur', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'cadre-marshal', count: 4 },
            { id: 'provocateur', count: 2 },
          ],
        },
      ],
    },
  ],
  bossId: 'comrade-vex',
  eliteId: 'cadre-captain',
  eliteWaveIndex: 2,
  // TODO(MY-1670): mid-stage civilian non-combatant placeholders deferred to crowd-system ticket.
  // TODO(MY-1670): Astor-Pemberton/Vex end cutscene deferred to Stage 6 cutscene ticket.
  props: [
    { propId: 'bethnal-placard', xFrac: 0.27 },
    { propId: 'bethnal-bin', xFrac: 0.5 },
    { propId: 'bethnal-placard', xFrac: 0.7 },
  ],
  healthPickups: [
    { xFrac: 0.38, heal: 10 },
    { xFrac: 0.6, heal: 10 },
  ],
};

const STAGE_7: StageConfig = {
  id: 7,
  name: 'DOVER COAST',
  parallax: {
    levelWidth: 3200,
    sky: 0x05080f,
    layers: [{ factor: 0.4, image: 'bg_s7_scene', color: 0x0c131f, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'rib-smuggler', count: 3 }] },
    {
      enemies: [
        { id: 'rib-smuggler', count: 2 },
        { id: 'cliff-spotter', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'rib-smuggler', count: 1 },
        { id: 'cliff-spotter', count: 2 },
      ],
    },
    { enemies: [{ id: 'cliff-spotter', count: 3 }] },
    {
      enemies: [
        { id: 'rib-smuggler', count: 2 },
        { id: 'cliff-spotter', count: 2 },
      ],
    },
  ],
  // MY-1792: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — Cliff Road (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'rib-smuggler', count: 3 }] },
        { enemies: [{ id: 'rib-smuggler', count: 4 }] },
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 3 },
            { id: 'cliff-spotter', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 4 },
            { id: 'cliff-spotter', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Beach (7 waves, beach-master elite at end, checkpoint after)
    {
      eliteId: 'beach-master',
      waves: [
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 3 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        { enemies: [{ id: 'cliff-spotter', count: 3 }] },
        {
          enemies: [
            { id: 'rib-smuggler', count: 3 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 3 },
          ],
        },
        { enemies: [{ id: 'cliff-spotter', count: 4 }] },
        {
          enemies: [
            { id: 'rib-smuggler', count: 4 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — The RIB Landing (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'rib-smuggler', count: 3 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 3 },
          ],
        },
        { enemies: [{ id: 'cliff-spotter', count: 4 }] },
        {
          enemies: [
            { id: 'rib-smuggler', count: 4 },
            { id: 'cliff-spotter', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'rib-smuggler', count: 2 },
            { id: 'cliff-spotter', count: 4 },
          ],
        },
        { enemies: [{ id: 'cliff-spotter', count: 5 }] },
        {
          enemies: [
            { id: 'rib-smuggler', count: 3 },
            { id: 'cliff-spotter', count: 3 },
          ],
        },
      ],
    },
  ],
  bossId: 'captain-vlad',
  eliteId: 'beach-master',
  eliteWaveIndex: 2,
  // TODO(MY-1671): intercept-ribs RIB marker spawner + scoring wired in StageScene follow-up.
  objective: 'intercept-ribs',
  props: [
    { propId: 'docks-crate', xFrac: 0.25 },
    { propId: 'docks-barrel', xFrac: 0.48 },
    { propId: 'docks-crate', xFrac: 0.68 },
  ],
  healthPickups: [{ xFrac: 0.38, heal: 10 }],
};

const STAGE_8: StageConfig = {
  id: 8,
  name: 'CANARY WHARF',
  parallax: {
    levelWidth: 3000,
    sky: 0x0a0f17,
    layers: [{ factor: 0.4, image: 'bg_s8_scene', color: 0x14202e, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'trading-floor-goon', count: 3 }] },
    {
      enemies: [
        { id: 'trading-floor-goon', count: 2 },
        { id: 'corporate-security', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'trading-floor-goon', count: 1 },
        { id: 'corporate-security', count: 2 },
      ],
    },
    { enemies: [{ id: 'corporate-security', count: 3 }] },
    {
      enemies: [
        { id: 'trading-floor-goon', count: 2 },
        { id: 'corporate-security', count: 2 },
      ],
    },
  ],
  // MY-1792: 3-area sections for ~20-min playthrough (20 waves total).
  sections: [
    // Area 1 — The Plaza (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'trading-floor-goon', count: 3 }] },
        { enemies: [{ id: 'trading-floor-goon', count: 4 }] },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 3 },
            { id: 'corporate-security', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 4 },
            { id: 'corporate-security', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Trading Floor (7 waves, floor-chief elite at end, checkpoint after)
    {
      eliteId: 'floor-chief',
      waves: [
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 3 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        { enemies: [{ id: 'corporate-security', count: 3 }] },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 3 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 3 },
          ],
        },
        { enemies: [{ id: 'corporate-security', count: 4 }] },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 4 },
            { id: 'corporate-security', count: 2 },
          ],
        },
      ],
    },
    // Area 3 — The Penthouse (7 waves; boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'trading-floor-goon', count: 3 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 3 },
          ],
        },
        { enemies: [{ id: 'corporate-security', count: 4 }] },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 4 },
            { id: 'corporate-security', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 2 },
            { id: 'corporate-security', count: 4 },
          ],
        },
        { enemies: [{ id: 'corporate-security', count: 5 }] },
        {
          enemies: [
            { id: 'trading-floor-goon', count: 3 },
            { id: 'corporate-security', count: 3 },
          ],
        },
      ],
    },
  ],
  bossId: 'the-hedge-fund',
  eliteId: 'floor-chief',
  eliteWaveIndex: 2,
  // TODO(MY-1672): lift-climb vertical-ascent scripted scroll arena wired in StageScene follow-up.
  hazard: 'lift-climb',
  props: [
    { propId: 'canary-glass-barrier', xFrac: 0.27 },
    { propId: 'canary-planter', xFrac: 0.5 },
    { propId: 'canary-glass-barrier', xFrac: 0.72 },
  ],
  healthPickups: [{ xFrac: 0.4, heal: 12 }],
};

const STAGE_9: StageConfig = {
  id: 9,
  name: 'WESTMINSTER',
  parallax: {
    levelWidth: 3200,
    sky: 0x0c0e1a,
    layers: [{ factor: 0.4, image: 'bg_s9_scene', color: 0x171a2b, topFrac: 1 }],
  },
  waves: [
    { enemies: [{ id: 'manor-praetorian', count: 3 }] },
    {
      enemies: [
        { id: 'manor-praetorian', count: 2 },
        { id: 'whitehall-fixer', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'manor-praetorian', count: 1 },
        { id: 'whitehall-fixer', count: 2 },
      ],
    },
    { enemies: [{ id: 'whitehall-fixer', count: 3 }] },
    {
      enemies: [
        { id: 'manor-praetorian', count: 2 },
        { id: 'whitehall-fixer', count: 2 },
        { id: 'bent-copper', count: 1 },
      ],
    },
    {
      enemies: [
        { id: 'manor-praetorian', count: 3 },
        { id: 'whitehall-fixer', count: 2 },
      ],
    },
  ],
  // MY-1792: 3-area sections for ~20-min playthrough (20 waves total).
  // Longest run-up to the Halloway+Astor finale; manor-praetorian are elite-tier so
  // wave counts stay lower (2–4) vs galsia stages.
  sections: [
    // Area 1 — Parliament Square (6 waves, checkpoint after)
    {
      waves: [
        { enemies: [{ id: 'manor-praetorian', count: 2 }] },
        { enemies: [{ id: 'manor-praetorian', count: 3 }] },
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 2 },
            { id: 'bent-copper', count: 1 },
          ],
        },
      ],
    },
    // Area 2 — The Corridor (7 waves, praetorian-captain elite at end, checkpoint after)
    {
      eliteId: 'praetorian-captain',
      waves: [
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'whitehall-fixer', count: 3 },
            { id: 'bent-copper', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 2 },
            { id: 'bent-copper', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'whitehall-fixer', count: 3 },
            { id: 'bent-copper', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 2 },
            { id: 'bent-copper', count: 1 },
          ],
        },
      ],
    },
    // Area 3 — The Chamber (7 waves; Halloway+Astor finale boss spawns after all waves clear)
    {
      waves: [
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 2 },
            { id: 'bent-copper', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'whitehall-fixer', count: 3 },
            { id: 'bent-copper', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 2 },
            { id: 'bent-copper', count: 1 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 2 },
            { id: 'whitehall-fixer', count: 3 },
            { id: 'bent-copper', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 4 },
            { id: 'whitehall-fixer', count: 2 },
          ],
        },
        {
          enemies: [
            { id: 'manor-praetorian', count: 3 },
            { id: 'whitehall-fixer', count: 3 },
            { id: 'bent-copper', count: 2 },
          ],
        },
      ],
    },
  ],
  // MY-1976: halloway-astor demoted to boss-rush slot in finaleGauntlet below.
  // Stage 9 no longer has a direct bossId — the gauntlet drives the finale.
  finaleGauntlet: STAGE_9_GAUNTLET,
  eliteId: 'praetorian-captain',
  eliteWaveIndex: 3,
  props: [
    { propId: 'westminster-barrier', xFrac: 0.25 },
    { propId: 'westminster-bin', xFrac: 0.48 },
    { propId: 'westminster-barrier', xFrac: 0.68 },
  ],
  healthPickups: [
    { xFrac: 0.35, heal: 15 },
    { xFrac: 0.58, heal: 15 },
  ],
};

/** Fallback used until later stages are specced. */
const DEFAULT_STAGE = STAGE_1;

const STAGES: Record<number, StageConfig> = {
  1: STAGE_1,
  2: STAGE_2,
  3: STAGE_3,
  4: STAGE_4,
  5: STAGE_5,
  6: STAGE_6,
  7: STAGE_7,
  8: STAGE_8,
  9: STAGE_9,
};

export function getStageConfig(stage: number): StageConfig {
  return STAGES[stage] ?? DEFAULT_STAGE;
}
